import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { clientAPI, matchingAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import ReportConcernModal from '../../components/client/ReportConcernModal';
import RatingFlow from '../../components/RatingFlow';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import MultiDayEventBadge from '../../components/MultiDayEventBadge';  // Iter-408
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ShiftEventCodeCard from '../../components/ShiftEventCodeCard';
import RequirementOrganizer from '../../components/RequirementOrganizer';
import ShiftTaskMonitor from '../../components/tasks/ShiftTaskMonitor';
import { EventPhotosPreview } from '../../components/client/EventPhotosPreview';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { ArrowLeft, MapPin, Calendar, Clock, Users, Star, MessageSquare, CheckCircle, Zap, TrendingUp, Info, Eye, QrCode, Bot } from 'lucide-react';
import { formatTime12h, formatTimeRange, formatFullDateTime12h, formatDateMonthDay } from '../../utils/timeFormat';
import AssignmentBadge from '../../components/AssignmentBadge';
import CoveragePanel from '../../components/shift/CoveragePanel';
import OnsiteArrivalCard from '../../components/shift/OnsiteArrivalCard';
import BookingStatusBadge from '../../components/BookingStatusBadge';
import { useTalentProfileModal } from '../../components/TalentProfileModalProvider';
import HaloRing from '../../components/HaloRing';
import { toast } from 'sonner';

// Match Score Badge Component
// Iter-234g — Client-side AI Visibility. Was branded generically as
// "Match" with a Zap icon; rebranded to "AI Match" so clients
// immediately recognize iEvntür is doing the talent matching for them.
// This is the single highest-leverage visibility surface on the client
// side — every shift detail page shows it on every applicant card.
const MatchScoreBadge = ({ score, summary, breakdown }) => {
  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (score >= 60) return 'bg-cyan-100 text-cyan-700 border-cyan-300';
    if (score >= 40) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Moderate';
    return 'Basic';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${getScoreColor(score)}`}
            data-testid="client-ai-match-badge"
          >
            <Bot className="w-3 h-3" />
            <span>AI Match {Math.round(score)}%</span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-medium">
              <Bot className="w-4 h-4 text-cyan-600" />
              iEvntür AI · {getScoreLabel(score)} Match ({Math.round(score)}%)
            </div>
            {summary && <p className="text-sm text-gray-600">{summary}</p>}
            {breakdown && (
              <div className="text-xs space-y-1 pt-2 border-t">
                {breakdown.role?.score >= 80 && <div>✓ Strong role match</div>}
                {breakdown.distance?.score >= 80 && <div>✓ Local to event</div>}
                {breakdown.reliability?.score >= 80 && <div>✓ High reliability</div>}
                {breakdown.experience?.score >= 80 && <div>✓ Experienced</div>}
                {breakdown.favorites?.score >= 50 && <div>✓ Previous hire or favorite</div>}
              </div>
            )}
            <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">
              Powered by iEvntür AI — uses role fit, distance, reliability, experience, and your favorites.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ShiftDetails = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const { open: openTalentProfile } = useTalentProfileModal();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [approvalData, setApprovalData] = useState({
    hours_worked: '',
    rating: 5,
    feedback: '',
  });
  // Match scores data
  const [applicantScores, setApplicantScores] = useState({});
  const [applicantSortNearest, setApplicantSortNearest] = useState(false);
  const [applicantRadius, setApplicantRadius] = useState(null); // null | 5|10|15|25|50
  const [rosterScores, setRosterScores] = useState({});
  const [loadingScores, setLoadingScores] = useState(false);

  useEffect(() => {
    fetchShiftDetails();
  }, [shiftId]);

  // Stripe success redirect handler — if the user just paid for a boost,
  // Stripe redirects to ?boost=success&session_id=cs_...  We call the
  // confirm endpoint inline as a fallback against webhook lag.
  const [searchParams, setSearchParams] = useSearchParams();
  // Iter-182 — event title inline editor state
  const [eventTitleEditOpen, setEventTitleEditOpen] = useState(false);
  const [eventTitleDraft, setEventTitleDraft] = useState('');
  const [eventTitleSaving, setEventTitleSaving] = useState(false);
  const [notifyBookedOnRename, setNotifyBookedOnRename] = useState(false);
  const [concernTarget, setConcernTarget] = useState(null);
  const [ratingTarget, setRatingTarget] = useState(null);
  // Iter-301a — Client cancel-gig state (declared up-front so React
  // hook order stays stable across early returns).
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  // Iter-305 — Client gig management parity (Edit / Archive / Delete).
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    shift_date: '',
    start_time: '',
    end_time: '',
    talent_needed: '',
    bill_rate: '',
    talent_pay_rate: '',
    venue_address: '',
    additional_notes: '',
  });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const boost = searchParams.get('boost');
    const sessionId = searchParams.get('session_id');
    if (boost === 'success' && sessionId) {
      (async () => {
        try {
          const r = await api.post(`/monetization/gig-boost/confirm/${sessionId}`);
          if (r.data?.activated) {
            toast.success('Boost activated!', {
              description: `Your gig is now featured for ${r.data.days} days (${r.data.scope}).`,
            });
          } else {
            toast.info('Boost payment received — activating shortly.');
          }
        } catch (err) {
          // Webhook may have already handled it; not fatal.
          if (err?.response?.status !== 400) {
            toast.warning('Boost confirmation pending — refresh in a moment.');
          }
        } finally {
          // Strip the query params so refreshes don't re-trigger.
          searchParams.delete('boost');
          searchParams.delete('session_id');
          searchParams.delete('type');
          setSearchParams(searchParams, { replace: true });
          fetchShiftDetails();
        }
      })();
    } else if (boost === 'cancelled') {
      toast.info('Boost checkout cancelled — no charge was made.');
      searchParams.delete('boost');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchShiftDetails = async () => {
    try {
      const response = await clientAPI.getShiftDetails(shiftId);
      setData(response.data);
      // Fetch match scores in the background
      fetchMatchScores();
    } catch (error) {
      // Iter-314c — do NOT bounce the user back to `/client/shifts` on
      // error. If the shift is archived/scoped-out/deleted, that list
      // often renders "No shifts yet" which looks like the app broke.
      // Show a persistent error state on THIS page with a clear back
      // link to the surface the user came from (invoices, briefing card,
      // etc. — via document.referrer).
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      setLoadError({
        status,
        message: typeof detail === 'string'
          ? detail
          : (detail?.message ||
             (status === 404 ? 'This event is no longer available.' :
              status === 403 ? "You don't have access to this event." :
              'Unable to load event details.')),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchScores = async () => {
    setLoadingScores(true);
    try {
      const [applicantsRes, rosterRes] = await Promise.all([
        matchingAPI.getApplicantsWithScores(shiftId).catch(() => null),
        matchingAPI.getRosterWithScores(shiftId).catch(() => null)
      ]);
      
      // Build lookup maps by talent_id
      if (applicantsRes?.data?.applicants) {
        const scores = {};
        applicantsRes.data.applicants.forEach(a => {
          scores[a.talent_id] = {
            score: a.match_score,
            summary: a.match_summary,
            breakdown: a.match_breakdown
          };
        });
        setApplicantScores(scores);
      }
      
      if (rosterRes?.data?.roster) {
        const scores = {};
        rosterRes.data.roster.forEach(r => {
          scores[r.talent_id] = {
            score: r.match_score,
            summary: r.match_summary
          };
        });
        setRosterScores(scores);
      }
    } catch (error) {
      console.log('Match scores not available:', error);
    } finally {
      setLoadingScores(false);
    }
  };

  const handleBookTalent = async (talentId) => {
    setBookingInProgress(true);
    try {
      await clientAPI.bookTalent({ shift_id: shiftId, talent_id: talentId });
      toast.success('Talent booked successfully! Message thread created.');
      fetchShiftDetails(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book talent');
    } finally {
      setBookingInProgress(false);
    }
  };

  // V413 — Open (or create) a message thread with any talent connected
  // to THIS shift. Works for booked, applicants, invitees, and admin-
  // added under-consideration talent. Fixes the SweetWater/Claire bug
  // where prospective talent had no reachable message button.
  const openMessageThread = async (talentId) => {
    if (!talentId) return;
    try {
      const res = await api.post('/messages/threads/from-event', {
        shift_id: shiftId,
        talent_id: talentId,
      });
      const threadId = res.data?.thread_id;
      if (!threadId) throw new Error('No thread returned');
      navigate(`/client/messages/${threadId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open conversation');
    }
  };

  const payInvoice = async () => {
    const inv = data?.invoice;
    if (!inv?.id) return;
    try {
      const res = await api.post(`/invoicing/checkout/${inv.id}`, { origin_url: window.location.origin });
      if (res.data?.url) window.location.href = res.data.url;
      else throw new Error('No checkout URL');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
    }
  };

  const copyPayLink = async () => {
    const inv = data?.invoice;
    if (!inv?.id) return;
    try {
      const res = await api.post(`/invoicing/checkout/${inv.id}`, { origin_url: window.location.origin });
      const url = res.data?.url;
      if (!url) throw new Error('No link');
      await navigator.clipboard.writeText(url);
      toast.success('Payment link copied');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not copy link');
    }
  };

  const handleApproveHours = async () => {
    if (!approvalData.hours_worked || approvalData.hours_worked <= 0) {
      toast.error('Please enter valid hours worked');
      return;
    }

    try {
      await clientAPI.approveHours(
        approvalDialog.booking_id,
        parseFloat(approvalData.hours_worked),
        approvalData.rating,
        approvalData.feedback
      );
      toast.success('Hours approved! Payout record created.');
      setApprovalDialog(null);
      setApprovalData({ hours_worked: '', rating: 5, feedback: '' });
      fetchShiftDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve hours');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Iter-314c — inline error card. Previously we bounced the user back
  // to `/client/shifts` on failure, which shows "No shifts yet" for
  // billing-only users and admins previewing as a client → looked like
  // the app was broken. Now we render context + a clear back link.
  if (loadError) {
    const backTo = document.referrer && document.referrer.includes('/invoices')
      ? '/client/invoices'
      : '/client/home';
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow-sm border border-rose-200 p-6" data-testid="shift-detail-error">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-900">Unable to open this event</h2>
                <p className="text-sm text-gray-600 mt-1" data-testid="shift-detail-error-message">
                  {loadError.message}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Event ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{shiftId}</code>
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(backTo)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium"
                    data-testid="shift-detail-error-back-btn"
                  >
                    ← Back to {backTo === '/client/invoices' ? 'invoices' : 'dashboard'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLoadError(null); setLoading(true); fetchShiftDetails(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    data-testid="shift-detail-error-retry-btn"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { shift, applications = [], bookings = [], invoice = null } = data;
  // Iter-157d — authoritative staffing count comes from bookings, not
  // from `applications.status === 'accepted'`. Admin-assigned,
  // profile-override, backup-activated and direct-hire bookings have
  // no application row, so the old filter showed "0/N" on any shift
  // that wasn't staffed through the apply → accept flow. Both the
  // count and the roster now derive from `bookings` which matches the
  // outer shift card's `bookings_count`.
  const activeBookings = bookings.filter((b) =>
    ['confirmed', 'checked_in', 'completed'].includes((b.status || '').toLowerCase())
  );
  const bookedCount = shift.bookings_count ?? activeBookings.length;
  const spotsRemaining = Math.max(0, (shift.talent_needed || 0) - bookedCount);
  const isFilled = spotsRemaining <= 0;

  // Iter-190 — booked talent must NOT appear under "New Applicants".
  // Filter out rows that already have a confirmed/active booking
  // (LEFT JOIN bookings → app.booking_id is non-null for booked rows).
  const pendingApplications = applications.filter(
    (app) => app.status === 'pending' && !app.booking_id,
  );
  const applicantsAnyDistance = pendingApplications.some(a => a.distance_available && a.distance_miles != null);
  const displayApplicants = (() => {
    let list = pendingApplications.slice();
    if (applicantRadius) list = list.filter(a => a.distance_available && a.distance_miles != null && a.distance_miles <= applicantRadius);
    if (applicantSortNearest) list.sort((x, y) => (x.distance_miles == null ? 1e9 : x.distance_miles) - (y.distance_miles == null ? 1e9 : y.distance_miles));
    return list;
  })();
  const bookedTalent = activeBookings;
  // Iter-133w — split client roster into Primary / Backup / Extra so the
  // client always knows who is the lead vs who is on standby.
  const bookedPrimary = bookedTalent.filter((a) => !a.assignment_type || a.assignment_type === 'primary');
  const bookedBackup  = bookedTalent.filter((a) => a.assignment_type === 'backup');
  const bookedExtra   = bookedTalent.filter((a) => a.assignment_type === 'extra');

  // Iter-213+ — Past events default to the "Booked Roster" tab. Previously
  // they opened to "Applicants" which is always empty for past shifts, so
  // clients thought details "weren't loading" when in fact the relevant
  // info (booked talent + hours) was just one tab over. Predicate matches
  // /client/shifts Past-tab filter (end_date elapsed OR terminal status).
  const _ymdLocal = (d) => {
    if (!d) return null;
    if (typeof d === 'string') return d.slice(0, 10);
    return d.toISOString().slice(0, 10);
  };
  const _todayYmd = new Date().toISOString().slice(0, 10);
  const _lastDayYmd = _ymdLocal(shift.end_date || shift.shift_date);
  const isPastEvent =
    ['completed', 'cancelled'].includes((shift.status || '').toLowerCase()) ||
    (_lastDayYmd && _lastDayYmd < _todayYmd);
  const defaultDetailTab = isPastEvent && bookedTalent.length > 0 ? 'booked' : 'applicants';

  // Iter-182 — open the title editor with the current value pre-loaded.
  const openEventTitleEditor = () => {
    setEventTitleDraft(shift?.event_title || '');
    setNotifyBookedOnRename(false);
    setEventTitleEditOpen(true);
  };

  // Iter-301a — Client cancel/delete handler. Soft-cancels via
  // `/api/client/shifts/{id}/cancel` which fires the talent
  // notification + admin audit row. Clients cannot hard-delete
  // (only super admins can) — once a shift exists in the system,
  // cancel preserves the audit trail.
  const canCancelShift = !isPastEvent
    && !['cancelled', 'completed'].includes((shift?.status || '').toLowerCase());
  const handleCancelShift = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await api.patch(`/client/shifts/${shiftId}/cancel`, {
        reason: cancelReason.trim() || 'client_cancelled',
      });
      toast.success('Gig cancelled. Assigned talent will be notified.');
      setCancelOpen(false);
      // Reload the shift so the banner reflects the new status.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel gig');
    } finally {
      setCancelling(false);
    }
  };
  const saveEventTitle = async () => {
    const v = (eventTitleDraft || '').trim();
    if (v.length < 5 || v.length > 120) {
      toast.error('Event title must be 5–120 characters');
      return;
    }
    setEventTitleSaving(true);
    try {
      await api.patch(`/shifts/${shiftId}/event-title`, {
        event_title: v,
        notify_booked_talent: notifyBookedOnRename,
      });
      toast.success('Event title updated');
      setEventTitleEditOpen(false);
      // Reload to surface the rename across the page (header, related cards).
      try { await fetchShiftDetails(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setEventTitleSaving(false);
    }
  };

  // ── Iter-305 — Client gig management (Edit / Archive / Delete) ────
  // Eligibility rules:
  //   • Edit:     Anything except completed (open / cancelled / filled OK).
  //   • Archive:  Anything not yet archived. Archived gigs show "Restore".
  //   • Delete:   ONLY when status in {open, cancelled} AND zero active
  //               bookings AND no invoice generated. Otherwise the
  //               business rule says "cancel + admin purges later".
  const statusLower = (shift?.status || '').toLowerCase();
  const canEditShift = !['completed'].includes(statusLower);
  const isArchived = !!shift?.is_archived;
  const hasActiveBookings = bookedCount > 0;
  const hasInvoice = !!shift?.invoice_generated_at;
  const canHardDelete = ['open', 'cancelled'].includes(statusLower)
    && !hasActiveBookings && !hasInvoice;

  const openEditDialog = () => {
    setEditForm({
      shift_date: shift?.shift_date ? String(shift.shift_date).slice(0, 10) : '',
      start_time: shift?.start_time ? String(shift.start_time).slice(0, 5) : '',
      end_time: shift?.end_time ? String(shift.end_time).slice(0, 5) : '',
      talent_needed: shift?.talent_needed ?? '',
      bill_rate: shift?.bill_rate ?? '',
      talent_pay_rate: shift?.talent_pay_rate ?? '',
      venue_address: shift?.venue_address || '',
      additional_notes: shift?.additional_notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (editSaving) return;
    // Light client-side validation.
    const tn = Number(editForm.talent_needed);
    const br = Number(editForm.bill_rate);
    const pr = Number(editForm.talent_pay_rate);
    if (!editForm.shift_date) { toast.error('Shift date is required'); return; }
    if (!editForm.start_time || !editForm.end_time) { toast.error('Start + end times are required'); return; }
    if (!(tn >= 1)) { toast.error('Talent needed must be ≥ 1'); return; }
    if (!(br > 0)) { toast.error('Bill rate must be greater than 0'); return; }
    if (!(pr > 0)) { toast.error('Talent pay rate must be greater than 0'); return; }
    setEditSaving(true);
    try {
      await api.patch(`/client/shifts/${shiftId}`, {
        shift_date: editForm.shift_date,
        start_time: editForm.start_time,
        end_time: editForm.end_time,
        talent_needed: tn,
        bill_rate: br,
        talent_pay_rate: pr,
        venue_address: editForm.venue_address || null,
        additional_notes: editForm.additional_notes || null,
      });
      toast.success('Gig updated. Booked talent will see the new details.');
      setEditOpen(false);
      try { await fetchShiftDetails(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update gig');
    } finally {
      setEditSaving(false);
    }
  };

  const handleArchive = async () => {
    if (archiving) return;
    setArchiving(true);
    try {
      if (isArchived) {
        await api.patch(`/client/shifts/${shiftId}/unarchive`);
        toast.success('Gig restored from trash.');
      } else {
        await api.patch(`/client/shifts/${shiftId}/archive`);
        toast.success('Gig archived (moved to trash).');
      }
      setArchiveOpen(false);
      try { await fetchShiftDetails(); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive gig');
    } finally {
      setArchiving(false);
    }
  };

  const handleHardDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/client/shifts/${shiftId}`);
      toast.success('Gig permanently deleted.');
      navigate('/client/shifts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete gig');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shift-details-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/client/shifts')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shifts
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Iter-213+ — Past-event summary banner so completed/cancelled shifts
            load the same operational detail as active ones (hours totals,
            approval count, recap + invoice CTAs). Previously past events
            opened on an empty Applicants tab with no post-event context. */}
        {isPastEvent && (
          <Card className="mb-6 border-slate-300 bg-slate-50" data-testid="shift-past-event-summary">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-slate-200 p-2">
                  <CheckCircle className="w-5 h-5 text-slate-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Past event — read-only
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {bookedTalent.length} talent booked · {' '}
                    {bookedTalent.reduce((sum, b) => sum + (Number(b.hours_worked) || 0), 0).toFixed(2)} total hours worked · {' '}
                    {bookedTalent.filter(b => b.hours_approved).length} approved
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/client/recap/${shiftId}`)}
                  data-testid="past-event-recap-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Event Recap
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/client/invoices')}
                  data-testid="past-event-invoice-btn"
                >
                  View Invoices
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shift Overview */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                {/* Iter-182 — Editable event title with inline pencil */}
                <CardTitle className="text-2xl mb-1 flex items-center gap-2" data-testid="client-shift-event-title">
                  {(shift.event_title || (shift.role_name ? `${shift.role_name} Gig` : (shift.venue_name || 'Shift Details')))}
                  <button
                    type="button"
                    onClick={openEventTitleEditor}
                    className="text-xs text-cyan-600 hover:text-cyan-700 underline ml-2 font-normal"
                    data-testid="edit-event-title-btn"
                  >
                    Edit
                  </button>
                </CardTitle>
                {shift.venue_name && (shift.event_title || shift.role_name) && (
                  <div className="text-sm text-gray-500 mb-2" data-testid="venue-subtitle">{shift.venue_name}</div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={shift.status === 'open' ? 'bg-green-100 text-green-800' : shift.status === 'filled' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                    {shift.status}
                  </Badge>
                  <MultiDayEventBadge
                    dayIndex={shift.day_index}
                    daysInGroup={shift.days_in_group}
                  />
                  {shift.tier_required === 'premium' && (
                    <Badge className="bg-purple-100 text-purple-800">Premium</Badge>
                  )}
                  <Badge className={isFilled ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'} data-testid="spots-remaining-badge">
                    <Users className="w-3 h-3 mr-1" />
                    {spotsRemaining} spots remaining
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">${shift.bill_rate}/hr</div>
                <div className="text-sm text-muted-foreground">Bill Rate</div>
                <div className="flex flex-wrap justify-end gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/client/job-overview/${shiftId}`)}
                    data-testid="view-overview-button"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Event Overview
                  </Button>
                  {canEditShift && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openEditDialog}
                      data-testid="edit-gig-button"
                    >
                      Edit Gig
                    </Button>
                  )}
                  {canCancelShift && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={() => setCancelOpen(true)}
                      data-testid="cancel-gig-button"
                    >
                      Cancel Gig
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setArchiveOpen(true)}
                    data-testid="archive-gig-button"
                  >
                    {isArchived ? 'Restore' : 'Move to Trash'}
                  </Button>
                  {canHardDelete && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-400 text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteOpen(true)}
                      data-testid="hard-delete-gig-button"
                    >
                      Delete Permanently
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{shift.city}, {shift.state}</div>
                  <div className="text-muted-foreground text-xs">{shift.venue_address}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{formatDateMonthDay(shift.shift_date)}</div>
                  <div className="text-muted-foreground text-xs">Shift Date</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{formatTimeRange(shift.start_time, shift.end_time)}</div>
                  <div className="text-muted-foreground text-xs">Time</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">{bookedCount} / {shift.talent_needed}</div>
                  <div className="text-muted-foreground text-xs">Booked</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live coverage — real talent confirmations only */}
        <div className="mb-6">
          <CoveragePanel shiftId={shiftId} refreshKey={bookedTalent.length} />
        </div>

        {/* Invoice & Payment — pay or copy a pay link right from the shift view */}
        {invoice && (
          <Card className="mb-6 border-cyan-200 bg-cyan-50/40" data-testid="shift-invoice-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900" data-testid="shift-invoice-number">
                    Invoice {invoice.invoice_number}
                  </div>
                  <div className="text-xs text-gray-600">
                    {invoice.payment_status === 'paid'
                      ? 'Paid — thank you'
                      : <>Amount due: <span className="font-semibold text-gray-900">${(invoice.amount_due ?? invoice.total_amount ?? 0).toFixed(2)}</span></>}
                  </div>
                  {invoice.client_change_note && (
                    <div className="mt-1 text-xs text-amber-700" data-testid="shift-invoice-change-note">
                      {invoice.client_change_note}
                    </div>
                  )}
                </div>
                {invoice.is_payable && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={copyPayLink} data-testid="shift-invoice-copy-link">
                      Copy pay link
                    </Button>
                    <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={payInvoice} data-testid="shift-invoice-pay-btn">
                      Pay now
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Requirement Organizer */}
        <div className="mb-6">
          <RequirementOrganizer basePath={`/client/shifts/${shiftId}`} entityLabel="shift" />
        </div>

        {/* Live task monitoring */}
        <div className="mb-6">
          <ShiftTaskMonitor shiftId={shiftId} pollMs={30000} />
        </div>

        {/* Tabs for Applicants and Booked */}
        <Tabs defaultValue={defaultDetailTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="applicants" data-testid="applicants-tab">
              Applicants ({pendingApplications.length})
            </TabsTrigger>
            <TabsTrigger value="booked" data-testid="booked-tab">
              Booked Roster ({bookedTalent.length})
            </TabsTrigger>
          </TabsList>

          {/* Iter-411k — Onsite event code visible to the shift owner
              (client). Provides the fallback code the client or their
              onsite lead can read to any talent whose GPS/QR check-in
              fails. Component silently hides itself for viewers without
              permission (backend 403 = card not rendered). */}
          <div className="mt-3">
            <ShiftEventCodeCard shiftId={shiftId} />
          </div>

          {/* iter417/iter418 — Onsite / Arrival Details (client-editable; Evie reads these) */}
          <div className="mt-3">
            <OnsiteArrivalCard
              shift={shift}
              method="patch"
              endpoint={`/client/shifts/${shiftId}`}
              canEdit={true}
              onSaved={() => fetchShiftDetails && fetchShiftDetails()}
            />
          </div>

          <TabsContent value="applicants" className="mt-6">
            {pendingApplications.length > 0 && (
              <div
                className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border border-cyan-200 bg-cyan-50/60 text-cyan-800"
                data-testid="ai-applicants-banner"
              >
                <Bot className="w-4 h-4 shrink-0" />
                <p className="text-xs">
                  <span className="font-semibold">Ranked by iEvntür AI</span>
                  {' '}— each applicant is scored on role fit, distance, reliability, experience, and your favorites.
                </p>
              </div>
            )}
            {pendingApplications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No pending applications
                </CardContent>
              </Card>
            ) : (
              <>
              {applicantsAnyDistance && (
                <div className="flex items-center gap-2 flex-wrap text-xs mb-3" data-testid="client-applicant-distance-toolbar">
                  <button
                    type="button"
                    onClick={() => setApplicantSortNearest(v => !v)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-semibold transition ${applicantSortNearest ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                    data-testid="client-applicant-sort-nearest"
                  >
                    <MapPin className="w-3 h-3" /> Nearest to venue
                  </button>
                  <span className="text-slate-400">Within:</span>
                  {[5, 10, 15, 25, 50].map(mi => (
                    <button
                      key={mi}
                      type="button"
                      onClick={() => setApplicantRadius(applicantRadius === mi ? null : mi)}
                      className={`px-2 py-1 rounded-full border font-semibold transition ${applicantRadius === mi ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                      data-testid={`client-applicant-radius-${mi}`}
                    >
                      {mi} mi
                    </button>
                  ))}
                  {applicantRadius && (
                    <button type="button" onClick={() => setApplicantRadius(null)} className="text-slate-500 underline" data-testid="client-applicant-radius-clear">clear</button>
                  )}
                </div>
              )}
              {displayApplicants.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground" data-testid="client-applicants-radius-empty">
                  No applicants within {applicantRadius} mi of the venue.
                </CardContent></Card>
              ) : (
              <div className="grid gap-4" data-testid="applicants-list">
                {displayApplicants.map((app) => {
                  const scoreData = applicantScores[app.talent_id];
                  const halo = HaloRing.computeFromBooking(app);
                  return (
                  <Card
                    key={app.id}
                    data-testid="applicant-card"
                    className={`cursor-pointer hover:shadow-md transition-shadow ${app.is_highlighted ? 'ring-1 ring-cyan-300 shadow-cyan-100' : ''}`}
                    onClick={() => app.talent_id && openTalentProfile?.(app.talent_id, { surface: 'applicant_list' })}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4">
                          <HaloRing state={halo.state} reason={halo.reason} size="md" testIdSuffix={`applicant-${app.talent_id}`}>
                            <Avatar
                              className="h-14 w-14"
                              data-testid={`applicant-avatar-${app.talent_id}`}
                            >
                              {app.profile_photo_url ? (
                                <AvatarImage
                                  src={app.profile_photo_url}
                                  alt={`${app.first_name} ${app.last_name}`}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              ) : null}
                              <AvatarFallback className="bg-cyan-100 text-cyan-700 text-lg font-semibold">
                                {(app.first_name || app.email || '?')[0]}{(app.last_name || '')[0] || ''}
                              </AvatarFallback>
                            </Avatar>
                          </HaloRing>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-lg hover:text-cyan-700 transition">{app.first_name} {app.last_name}</h3>
                              {scoreData && (
                                <MatchScoreBadge
                                  score={scoreData.score}
                                  summary={scoreData.summary}
                                  breakdown={scoreData.breakdown}
                                />
                              )}
                              {app.is_highlighted && (
                                <Badge className="bg-gradient-to-r from-cyan-500 to-sky-600 text-white border-0 text-[10px] font-semibold" data-testid={`applicant-featured-${app.talent_id}`}>
                                  Featured
                                </Badge>
                              )}
                              {app.idv_status === 'approved' && (
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold" data-testid={`applicant-idv-${app.talent_id}`}>
                                  ID Verified
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 flex-wrap">
                              <Badge variant="outline">{app.tier === 'premium' ? 'Premium' : 'Flex'}</Badge>
                              {(app.city || app.state) && (
                                <>
                                  <span>•</span>
                                  <span data-testid={`applicant-location-${app.talent_id}`}>
                                    {[app.city, app.state].filter(Boolean).join(', ')}
                                  </span>
                                </>
                              )}
                              {app.distance_available && app.distance_miles != null && (
                                <>
                                  <span>•</span>
                                  <span className="font-semibold text-cyan-700" data-testid={`applicant-distance-${app.talent_id}`}>
                                    {app.distance_approx ? '~' : ''}{app.distance_miles} mi from venue
                                  </span>
                                </>
                              )}
                              {app.total_completed_gigs > 0 && (
                                <>
                                  <span>•</span>
                                  <span data-testid={`applicant-gigs-${app.talent_id}`}>{app.total_completed_gigs} completed</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-sm flex-wrap">
                              <div className="flex items-center gap-1" title="Reliability score">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="font-medium">{app.reliability_score}%</span>
                                <span className="text-muted-foreground text-xs">Reliability</span>
                              </div>
                              {app.average_rating != null && app.total_ratings > 0 && (
                                <div className="flex items-center gap-1" data-testid={`applicant-rating-${app.talent_id}`} title="Client ratings">
                                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                                  <span className="font-medium">{app.average_rating.toFixed(1)}</span>
                                  <span className="text-muted-foreground text-xs">({app.total_ratings} reviews)</span>
                                </div>
                              )}
                            </div>
                            {app.certifications && app.certifications.length > 0 && (
                              <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`applicant-certs-${app.talent_id}`}>
                                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Certified:</span>
                                {app.certifications.slice(0, 4).map((cert) => (
                                  <Badge key={cert} variant="outline" className="text-[10px] border-cyan-200 text-cyan-700 bg-cyan-50/60">
                                    {cert.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                                {app.certifications.length > 4 && (
                                  <span className="text-[10px] text-gray-500">+{app.certifications.length - 4} more</span>
                                )}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground mt-2">
                              Applied: {formatFullDateTime12h(app.applied_at)}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMessageThread(app.talent_id)}
                            data-testid="applicant-message-button"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleBookTalent(app.talent_id); }}
                            disabled={bookingInProgress || isFilled}
                            className="bg-cyan-500 hover:bg-cyan-600"
                            data-testid="book-talent-button"
                          >
                            {bookingInProgress ? 'Booking...' : 'Book'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
              )}
              </>
            )}
          </TabsContent>

          <TabsContent value="booked" className="mt-6">
            <Card className="mb-6" data-testid="client-event-photos-card">
              <CardContent className="pt-6">
                <EventPhotosPreview shiftId={shiftId} />
              </CardContent>
            </Card>
            {bookedTalent.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No booked talent yet
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6" data-testid="booked-roster">
                {[
                  { key: 'primary', list: bookedPrimary, type: 'primary' },
                  { key: 'backup',  list: bookedBackup,  type: 'backup' },
                  { key: 'extra',   list: bookedExtra,   type: 'extra' },
                ].filter(g => g.list.length > 0).map(group => (
                  <div key={group.key} data-testid={`client-roster-${group.key}`}>
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b">
                      <AssignmentBadge type={group.type} size="md" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        ({group.list.length})
                      </span>
                    </div>
                    <div className="grid gap-4">
                      {group.list.map((app) => {
                  const scoreData = rosterScores[app.talent_id];
                  const halo = HaloRing.computeFromBooking(app);
                  return (
                  <Card
                    key={app.id}
                    data-testid="booked-talent-card"
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => app.talent_id && openTalentProfile?.(app.talent_id, { surface: 'applicant_list' })}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-4">
                          <HaloRing state={halo.state} reason={halo.reason} size="md" testIdSuffix={`booked-${app.talent_id}`}>
                            <Avatar
                              className="h-12 w-12"
                              data-testid={`booked-avatar-${app.talent_id}`}
                            >
                              {app.profile_photo_url ? (
                                <AvatarImage
                                  src={app.profile_photo_url}
                                  alt={app.first_name}
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              ) : null}
                              <AvatarFallback className="bg-green-100 text-green-700 text-lg font-semibold">
                                {(app.first_name || app.email || '?')[0]}{(app.last_name || '')[0] || ''}
                              </AvatarFallback>
                            </Avatar>
                          </HaloRing>
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-lg">{app.first_name} {app.last_name}</h3>
                              {(app.is_confirmed || ['confirmed', 'day_confirmed'].includes((app.confirmation_status || '').toLowerCase()))
                                ? <CheckCircle className="w-5 h-5 text-green-500" />
                                : <Clock className="w-5 h-5 text-amber-500" />}
                              <AssignmentBadge type={app.assignment_type || 'primary'} size="md" />
                              {scoreData && (
                                <MatchScoreBadge score={scoreData.score} summary={scoreData.summary} />
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <BookingStatusBadge booking={app} size="md" />
                              {app.tier === 'premium' && (
                                <Badge className="bg-purple-100 text-purple-800">Premium</Badge>
                              )}
                              {/* Iter-213+ — surface hours worked + approval state directly
                                  on the roster row so past events show what was delivered. */}
                              {app.hours_worked != null && Number(app.hours_worked) > 0 && (
                                <Badge className="bg-slate-100 text-slate-700 border-0" data-testid={`roster-hours-${app.booking_id}`}>
                                  <Clock className="w-3 h-3 mr-1" />
                                  {Number(app.hours_worked).toFixed(2)} hrs
                                </Badge>
                              )}
                              {app.hours_approved && (
                                <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid={`roster-approved-${app.booking_id}`}>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMessageThread(app.talent_id)}
                            data-testid="message-button"
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                          {new Date(shift.shift_date) < new Date() && !app.hours_approved && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setApprovalDialog({
                                  booking_id: app.booking_id,
                                  talent_name: `${app.first_name} ${app.last_name}`,
                                  hours_worked: app.hours_worked || '',
                                });
                                setApprovalData({
                                  hours_worked: app.hours_worked || '',
                                  rating: 5,
                                  feedback: '',
                                });
                              }}
                              className="bg-cyan-500 hover:bg-cyan-600"
                              data-testid="approve-hours-button"
                            >
                              Approve Hours
                            </Button>
                          )}
                          {app.hours_approved && (
                            <Badge className="bg-blue-100 text-blue-800">Hours Approved</Badge>
                          )}
                          {/* Standalone rating entry — available for any past/completed
                              assignment, independent of the hours-approval flow, so
                              clients can always rate (or see they've rated) their talent. */}
                          {app.booking_id && (new Date(shift.shift_date) < new Date() || ['completed','checked_in'].includes((app.status || '').toLowerCase())) && (
                            app.is_rated ? (
                              <Badge className="bg-amber-100 text-amber-800 border-0 gap-1" data-testid={`rated-badge-${app.booking_id}`}>
                                <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Rated
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => setRatingTarget({
                                  booking_id: app.booking_id,
                                  name: `${app.first_name || ''} ${app.last_name || ''}`.trim(),
                                  role_name: shift.role_name || shift.role_title,
                                  company_name: shift.venue_name,
                                })}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid={`rate-talent-btn-${app.booking_id}`}
                              >
                                <Star className="w-4 h-4 mr-2" />
                                Rate Talent
                              </Button>
                            )
                          )}
                          {app.booking_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-700 hover:bg-amber-50 justify-start px-2"
                              onClick={() => setConcernTarget({ booking_id: app.booking_id, talent_name: `${app.first_name} ${app.last_name}` })}
                              data-testid={`report-concern-btn-${app.booking_id}`}
                            >
                              Report a Concern
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <ReportConcernModal
        open={!!concernTarget}
        onClose={() => setConcernTarget(null)}
        bookingId={concernTarget?.booking_id}
        talentName={concernTarget?.talent_name}
      />

      <RatingFlow
        isOpen={!!ratingTarget}
        onClose={() => setRatingTarget(null)}
        booking={ratingTarget}
        ratingType="client_to_talent"
        onRatingSubmitted={() => { setRatingTarget(null); fetchShiftDetails(); }}
      />

      {/* Hours Approval Dialog */}
      <Dialog open={!!approvalDialog} onOpenChange={() => setApprovalDialog(null)}>
        <DialogContent>
          {approvalDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Approve Hours - {approvalDialog.talent_name}</DialogTitle>
                <DialogDescription>
                  Review and approve the hours worked for this shift
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="hours_worked">Hours Worked *</Label>
                  <Input
                    id="hours_worked"
                    type="number"
                    step="0.25"
                    min="0"
                    value={approvalData.hours_worked}
                    onChange={(e) => setApprovalData({ ...approvalData, hours_worked: e.target.value })}
                    placeholder="8.0"
                    data-testid="hours-worked-input"
                  />
                  {approvalData.hours_worked && (
                    <p className="text-sm text-muted-foreground">
                      Hours logged: {approvalData.hours_worked}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Rating (1-5 stars)</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setApprovalData({ ...approvalData, rating: star })}
                        className="focus:outline-none"
                        data-testid={`rating-${star}`}
                      >
                        <Star
                          className={`w-8 h-8 ${star <= approvalData.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback">Feedback (Optional)</Label>
                  <Textarea
                    id="feedback"
                    value={approvalData.feedback}
                    onChange={(e) => setApprovalData({ ...approvalData, feedback: e.target.value })}
                    placeholder="Great work! Very professional..."
                    rows={3}
                    data-testid="feedback-input"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setApprovalDialog(null)}>
                  Cancel
                </Button>
                <Button onClick={handleApproveHours} className="bg-cyan-500 hover:bg-cyan-600" data-testid="submit-approval-button">
                  Approve & Create Payout
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter-301a — Client cancel gig confirmation modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-gig-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this gig?</DialogTitle>
            <DialogDescription>
              Assigned talent will be notified immediately. The gig will be
              marked Cancelled and remain in your history — invoices and
              audit trail are preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="cancel-reason">Reason (optional)</Label>
            <Input
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Event postponed, weather, venue change"
              data-testid="cancel-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep Gig
            </Button>
            <Button
              onClick={handleCancelShift}
              disabled={cancelling}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-cancel-gig-btn"
            >
              {cancelling ? 'Cancelling…' : 'Cancel Gig'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-182 — Event Title editor modal */}
      <Dialog open={eventTitleEditOpen} onOpenChange={setEventTitleEditOpen}>
        <DialogContent data-testid="event-title-editor-dialog">
          <DialogHeader>
            <DialogTitle>Edit Event Title</DialogTitle>
            <DialogDescription>
              Talent see this everywhere — discover cards, push notifications, email subjects, and the event channel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="event-title-edit">Event Title</Label>
            <Input
              id="event-title-edit"
              value={eventTitleDraft}
              onChange={(e) => setEventTitleDraft(e.target.value)}
              maxLength={120}
              placeholder="e.g. VIP Mansion Cocktail Party"
              data-testid="event-title-edit-input"
            />
            <p className="text-xs text-gray-500">{(eventTitleDraft || '').trim().length}/120 (5-120 required)</p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notifyBookedOnRename}
                onChange={(e) => setNotifyBookedOnRename(e.target.checked)}
                data-testid="notify-booked-toggle"
              />
              Notify booked talent of this rename
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventTitleEditOpen(false)}>Cancel</Button>
            <Button
              onClick={saveEventTitle}
              disabled={eventTitleSaving}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="event-title-save-btn"
            >
              {eventTitleSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-305 — Edit Gig dialog (date / time / rates / staffing / notes) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-gig-dialog">
          <DialogHeader>
            <DialogTitle>Edit Gig</DialogTitle>
            <DialogDescription>
              Changes save immediately. Booked talent will see the new
              details and receive a rate-change or time-change notification
              when applicable.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-shift-date">Shift Date</Label>
              <Input
                id="edit-shift-date"
                type="date"
                value={editForm.shift_date}
                onChange={(e) => setEditForm({ ...editForm, shift_date: e.target.value })}
                data-testid="edit-shift-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-start-time">Start Time</Label>
              <Input
                id="edit-start-time"
                type="time"
                value={editForm.start_time}
                onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                data-testid="edit-start-time-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-end-time">End Time</Label>
              <Input
                id="edit-end-time"
                type="time"
                value={editForm.end_time}
                onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                data-testid="edit-end-time-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-talent-needed">Talent Needed</Label>
              <Input
                id="edit-talent-needed"
                type="number"
                min={1}
                value={editForm.talent_needed}
                onChange={(e) => setEditForm({ ...editForm, talent_needed: e.target.value })}
                data-testid="edit-talent-needed-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-bill-rate">Bill Rate ($/hr)</Label>
              <Input
                id="edit-bill-rate"
                type="number"
                min={0}
                step="0.01"
                value={editForm.bill_rate}
                onChange={(e) => setEditForm({ ...editForm, bill_rate: e.target.value })}
                data-testid="edit-bill-rate-input"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-pay-rate">Talent Pay Rate ($/hr)</Label>
              <Input
                id="edit-pay-rate"
                type="number"
                min={0}
                step="0.01"
                value={editForm.talent_pay_rate}
                onChange={(e) => setEditForm({ ...editForm, talent_pay_rate: e.target.value })}
                data-testid="edit-pay-rate-input"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-venue-address">Venue Address</Label>
              <Input
                id="edit-venue-address"
                value={editForm.venue_address}
                onChange={(e) => setEditForm({ ...editForm, venue_address: e.target.value })}
                data-testid="edit-venue-address-input"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="edit-additional-notes">Additional Notes</Label>
              <Textarea
                id="edit-additional-notes"
                rows={3}
                value={editForm.additional_notes}
                onChange={(e) => setEditForm({ ...editForm, additional_notes: e.target.value })}
                data-testid="edit-additional-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={editSaving}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="edit-gig-save-btn"
            >
              {editSaving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-305 — Archive (Move to Trash) confirmation */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent data-testid="archive-gig-dialog">
          <DialogHeader>
            <DialogTitle>{isArchived ? 'Restore this gig?' : 'Move this gig to Trash?'}</DialogTitle>
            <DialogDescription>
              {isArchived
                ? 'The gig will reappear in your active dashboard and become visible to admin views.'
                : 'The gig is hidden from your active dashboard but kept in your history. Use Restore to bring it back.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={archiving}>
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={archiving}
              className="bg-slate-700 hover:bg-slate-800 text-white"
              data-testid="confirm-archive-gig-btn"
            >
              {archiving ? 'Working…' : (isArchived ? 'Restore' : 'Move to Trash')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-305 — Hard-delete confirmation. Only renders when the
          business-rule guard returned canHardDelete=true. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-gig-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete this gig?</DialogTitle>
            <DialogDescription>
              This cannot be undone. The gig and any of its applications,
              invitations, and notifications will be removed. We allow this
              only because there are no active bookings, no invoice has been
              issued, and the gig is still open or cancelled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Keep It
            </Button>
            <Button
              onClick={handleHardDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-hard-delete-gig-btn"
            >
              {deleting ? 'Deleting…' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftDetails;